// Exchange Rates Service
// Uses Open Exchange Rates API with local caching

const OER_APP_ID = process.env.REACT_APP_OER_APP_ID;
const OER_BASE_URL = 'https://openexchangerates.org/api';
const CACHE_KEY = 'myfinance_exchange_rates';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export const SUPPORTED_CURRENCIES = [
  'KWD','USD','EUR','GBP','TRY','AED','SAR','EGP','JOD','BHD',
  'OMR','QAR','INR','PKR','LBP','CAD','AUD','CHF','JPY','CNY',
  'SGD','MYR','THB','SEK','NOK','DKK','NZD','ZAR','NGN','KES'
];

// Default fallback rates (USD base) — used ONLY if live API fails
// Updated as of June 2026 — these are emergency fallback values only
const FALLBACK_RATES = {
  USD: 1,
  KWD: 0.3074,
  EUR: 0.9210,
  GBP: 0.7880,
  TRY: 46.30,
};

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    // Safety check: if cached TRY rate looks stale/wrong (below 40), discard cache
    // This guards against old cached fallback values from previous app versions
    if (parsed.rates?.TRY && parsed.rates.TRY < 40) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return { rates: parsed.rates, base: parsed.base, timestamp: parsed.timestamp };
  } catch {
    return null;
  }
}

function setCache(rates, base) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      rates,
      base,
      timestamp: Date.now(),
    }));
  } catch {}
}

// Fetch latest rates from OER (base: USD)
export async function fetchExchangeRates() {
  const cached = getCache();
  if (cached) return cached;

  try {
    const res = await fetch(
      `${OER_BASE_URL}/latest.json?app_id=${OER_APP_ID}&symbols=${SUPPORTED_CURRENCIES.join(',')}`
    );
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
    }
    const data = await res.json();
    if (!data.rates || !data.rates.TRY) {
      throw new Error('Response missing expected rate fields');
    }

    const result = { rates: data.rates, base: 'USD', timestamp: Date.now() };
    setCache(data.rates, 'USD');
    return result;
  } catch (err) {
    // Log full detail to console so the cause (expired key, quota, CORS) is visible in devtools
    console.error('[ExchangeRates] Live fetch failed, using fallback rates. Reason:', err.message);
    const fallback = { rates: FALLBACK_RATES, base: 'USD', timestamp: Date.now(), isFallback: true, fallbackReason: err.message };
    setCache(FALLBACK_RATES, 'USD');
    return fallback;
  }
}

// Convert amount from currency A → B using fetched rates (USD base)
export function convertCurrency(amount, fromCurrency, toCurrency, rates) {
  if (fromCurrency === toCurrency) return amount;
  if (!rates) return amount;

  const fromRate = rates[fromCurrency] || 1;
  const toRate = rates[toCurrency] || 1;

  // Convert to USD first, then to target
  const inUSD = amount / fromRate;
  return inUSD * toRate;
}

// Get rate from any currency → KWD
export function rateToKWD(fromCurrency, rates) {
  if (fromCurrency === 'KWD') return 1;
  if (!rates) return 1;
  const fromRate = rates[fromCurrency] || 1;
  const kwdRate = rates['KWD'] || 0.3074;
  return kwdRate / fromRate;
}

// Curated currency symbols — used instead of Intl's built-in currency
// formatting because many currencies this app supports (KWD, TRY, and most
// Gulf currencies) have no recognized glyph in Intl/ICU and silently fall
// back to rendering the raw 3-letter ISO code glued straight to the number
// (e.g. "KWD1,234.567") — inconsistent-looking and dependent on the
// browser's own CLDR data. This is the single canonical symbol source —
// SettingsPage's currency picker (CURRENCY_META) reads its `symbol` field
// from here too, rather than keeping its own separate copy.
export const CURRENCY_SYMBOLS = {
  KWD: 'KD', USD: '$', EUR: '€', GBP: '£', TRY: '₺',
  AED: 'د.إ', SAR: '﷼', EGP: 'E£', JOD: 'JD', BHD: 'BD',
  OMR: 'ر.ع.', QAR: 'ر.ق', INR: '₹', PKR: '₨', LBP: 'ل.ل',
  CAD: 'C$', AUD: 'A$', CHF: 'Fr', JPY: '¥', CNY: '¥',
  SGD: 'S$', MYR: 'RM', THB: '฿', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', NZD: 'NZ$', ZAR: 'R', NGN: '₦', KES: 'KSh',
};

// Format currency for display. The symbol is glued directly to the number
// for single-character glyphs ($, €, £, ₺, ¥, ₹, ฿, ₦ ...) matching
// universal convention, and separated by a thin space for multi-character
// symbols (KD, JD, kr, and the native-script Arabic abbreviations) —
// gluing those would visually run them into the digits, and for
// right-to-left script it risks bidi ordering issues too.
export function formatCurrency(amount, currency = 'KWD', options = {}) {
  const decimals = currency === 'KWD' ? 3 : 2;
  const number = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.compact ? 0 : decimals,
    maximumFractionDigits: options.compact ? 2 : decimals,
    notation: options.compact ? 'compact' : 'standard',
  }).format(amount || 0);

  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const isGlyph = Array.from(symbol).length === 1;
  const sign = number.startsWith('-') ? '-' : '';
  const digits = sign ? number.slice(1) : number;
  return isGlyph ? `${sign}${symbol}${digits}` : `${sign}${symbol} ${digits}`;
}

// Format without currency symbol
export function formatAmount(amount, currency = 'KWD') {
  const decimals = currency === 'KWD' ? 3 : 2;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount || 0);
}

// Returns SUPPORTED_CURRENCIES with favourites sorted to the top.
// Used in currency pickers throughout the app.
export function getSortedCurrencies(favouriteCurrencies = []) {
  const favSet = new Set(favouriteCurrencies);
  const favs   = SUPPORTED_CURRENCIES.filter(c => favSet.has(c));
  const rest   = SUPPORTED_CURRENCIES.filter(c => !favSet.has(c));
  return [...favs, '---', ...rest]; // '---' is a visual separator sentinel
}
